import React, { useState } from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import ProgressiveImage from '@ui/ProgressiveImage.tsx';
import Tooltip from '@ui/tooltip';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type GalleryImage = {
  url: string;
  alternativeText?: string;
};

type GalleryProps = {
  images: GalleryImage[];
  T: TranslateFn;
  name?: string;
};

export const GalleryWrapper = styled.div`
  position: relative;
`;

const ControllerWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
`;

const LeftControllerWrapper = styled(ControllerWrapper)`
  inset-inline-start: 8px;
`;

const RightControllerWrapper = styled(ControllerWrapper)`
  inset-inline-end: 8px;
`;

export const Controller = styled.button`
  background: var(--color-primaryButton-85);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;

  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);

  &:focus-visible {
    outline-offset: 2px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: white;
  }

  &:hover {
    background: var(--color-primaryButton);
  }
`;

export const GalleryControllerIcon = styled(Icon)`
  cursor: pointer;
  fill: var(--color-primaryButtonText);
  color: var(--color-primaryButtonText);
`;

const Image = styled(ProgressiveImage)`
  width: 100%;
  height: 100%;

  img {
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    width: 100%;
    height: auto;
    border-radius: ${({ theme }) => theme.standardRadius};
  }
`;

export const ImageNumberIndicator = styled.div`
  position: absolute;
  display: block;

  background: var(--color-primaryButton-85);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;

  top: 8px;
  inset-inline-end: 8px;
  width: 52px;
  height: 22px;

  color: var(--color-primaryButtonText);
  text-align: center;
  line-height: 22px;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize('12px')};
  font-family: ${({ theme }) => theme.fonts.family};
`;

const ImagesWrapper = styled.div`
  height: 100%;
  transition: transform 0.45s ease-out 0s;
  width: auto;
  display: flex;
`;

const Slide = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1 0 auto;
`;
/**
 *
 * @param {*} images : { url: string, alternativeText?: string }
 * @param {*} T : Translate Function
 * @param {*} name : string
 * @returns
 */
export function images(images: GalleryImage[], T: TranslateFn, name?: string) {
  if (images.length === 0) {
    return null;
  }
  if (images.length === 1) {
    return (
      <Image
        src={images[0].url}
        alt={images[0].alternativeText || (name ? `${name} ` : ``) + T('ui:Storefront photo')}
      />
    );
  }
  return <Gallery images={images} T={T} name={name} />;
}

const Gallery = ({ images, T, name }: GalleryProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);

  const imageNumberIndicatorText = `${currentImageIndex + 1} of ${images.length}`;

  const nextClicked = () => {
    let nextImageIndex = currentImageIndex + 1;
    let nextTranslateValue = translateValue - slideWidth();

    if (nextImageIndex === images.length) {
      nextImageIndex = 0;
      nextTranslateValue = 0;
    }

    setCurrentImageIndex(nextImageIndex);
    setTranslateValue(nextTranslateValue);
  };

  const previousClicked = () => {
    let previousImageIndex = currentImageIndex - 1;
    let previousTranslateValue = translateValue + slideWidth();

    if (previousImageIndex < 0) {
      previousImageIndex = images.length - 1;
      previousTranslateValue = -(previousImageIndex * slideWidth()); // Slide through to the last img
    }

    setCurrentImageIndex(previousImageIndex);
    setTranslateValue(previousTranslateValue);
  };

  const slideWidth = () => {
    return document.querySelector('.gallery-wrapper')?.clientWidth ?? 0;
  };

  return (
    <GalleryWrapper
      data-testid="GalleryWrapper"
      className="gallery-wrapper"
      role="group"
      aria-roledescription="carousel"
      aria-label={T('ui:Storefront images')}
      tabIndex={-1}
    >
      <ImagesWrapper
        style={{
          transform: `translateX(${translateValue}px)`,
        }}
      >
        {images.map((image, index) => (
          <Slide
            key={index}
            role="group"
            aria-roledescription="slide"
            aria-label={`Image ${index + 1} of ${images.length}`}
          >
            <Image
              key={index}
              isShowing={index === currentImageIndex}
              src={image.url}
              alt={
                image.alternativeText ||
                (name ? `${name} ` : ``) + T('ui:Storefront photo') + ` #${index + 1} / ${images.length}`
              }
            />
          </Slide>
        ))}
      </ImagesWrapper>

      <LeftControllerWrapper>
        <Tooltip text={T('ui:Prev Photo')} arrowPosition="top">
          <Controller onClick={previousClicked} data-testid="GalleryPrevButton" aria-label={T('ui:Prev Photo')}>
            <GalleryControllerIcon cdn name="chevron-left" width={24} height={24} />
          </Controller>
        </Tooltip>
      </LeftControllerWrapper>

      <RightControllerWrapper>
        <Tooltip text={T('ui:Next Photo')} arrowPosition="top">
          <Controller onClick={nextClicked} data-testid="GalleryNextButton" aria-label={T('ui:Next Photo')}>
            <GalleryControllerIcon cdn name="chevron-right" width={24} height={24} />
          </Controller>
        </Tooltip>
      </RightControllerWrapper>

      <ImageNumberIndicator>{imageNumberIndicatorText}</ImageNumberIndicator>
    </GalleryWrapper>
  );
};
